import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Box,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  FormControl,
  InputLabel
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid2";

import axios from "../../api/axios";
import GeneralContext from "../GeneralContext/GeneralContext";
import PageBody from "../GeneralComponents/PageBody";
import HourglassLoader from "../GeneralComponents/HourglassLoader";
import {
  ROL_OP_ADMIN,
  ROL_OP_INS,
  ROL_ADMIN_SUBTEL,
  ROL_VAL_SUBTEL,
  ROL_ADMIN
} from "../../Constants/Constants";

export default function ReportePromedioMensual() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const { userSesion, includeRol } = useContext(GeneralContext);

  const [zona, setZona] = useState([]);
  const [rbd, setRbd] = useState(null);
  const [operador, setOperador] = useState("");
  const [operadores, setOperadores] = useState([]);

  const esOperador = includeRol([ROL_OP_ADMIN, ROL_OP_INS]);
  const esSubtelOAdmin = includeRol([
    ROL_ADMIN_SUBTEL,
    ROL_VAL_SUBTEL,
    ROL_ADMIN
  ]);

  const panelIds = [5, 4, 3, 2, 1];
  const baseUrl =
    "/service/grafana/d-solo/ffgx0uja7qccgc/promedio-ventanas";
  const fixedFrom = "1735700400000";

  const getFirstAvailableRbd = (zonas) => {
    const allRbds = zonas.flatMap((item) => item.rbd_list || []);
    return allRbds.length > 0 ? allRbds[0] : null;
  };

  const fetchData = async (id) => {
    setLoading(true);
    try {
      const response = await axios.getZonasOperador(id);
      const newZonas = (response.data.zonas || []).map((zona) => ({
        ...zona,
        nombre_zona: `ZONA${String(zona.id_zona).padStart(2, "0")}`
      }));

      setRows(newZonas);
      setZona([]);

      const firstRbd = getFirstAvailableRbd(newZonas);
      setRbd(firstRbd);
    } catch (error) {
      console.error("Error en fetchData:", error);
      setRows([]);
      setZona([]);
      setRbd(null);
    } finally {
      setLoading(false);
    }
  };

  const getOperator = async () => {
    setLoading(true);
    try {
      const response = await axios.getOperator(userSesion.id);
      setOperador(response.data.operador);
      await fetchData(response.data.id_operador);
    } catch (error) {
      console.error("Error en getOperator:", error);
      setLoading(false);
    }
  };

  const getAllOperadores = async () => {
    setLoading(true);
    try {
      const response = await axios.getAllOperadores();
      const list = response.data || [];
      setOperadores(list);

      if (list.length > 0) {
        setOperador(list[0].nombre_operador);
        await fetchData(list[0].id_operador);
      } else {
        setRows([]);
        setZona([]);
        setRbd(null);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error en getAllOperadores:", error);
      setLoading(false);
    }
  };

  const handleZonaChange = (event) => {
    const selectedZonas = event.target.value;
    setZona(selectedZonas);

    let filteredRBDs = [];

    if (selectedZonas.length === 0) {
      filteredRBDs = rows.flatMap((row) => row.rbd_list || []);
    } else {
      filteredRBDs = rows
        .filter((row) => selectedZonas.includes(row.nombre_zona))
        .flatMap((row) => row.rbd_list || []);
    }

    if (filteredRBDs.length === 0) {
      setRbd(null);
      return;
    }

    if (!rbd || !filteredRBDs.includes(rbd)) {
      setRbd(filteredRBDs[0]);
    }
  };

  const handleRbdChange = (_, value) => {
    setRbd(value);
  };

  const handleOperadorChange = async (event) => {
    const value = event.target.value;
    setOperador(value);

    const op = operadores.find((o) => o.nombre_operador === value);
    if (op) {
      await fetchData(op.id_operador);
    }
  };

  const availableRBDs = useMemo(() => {
    if (zona.length === 0) {
      return rows.flatMap((row) => row.rbd_list || []);
    }

    return rows
      .filter((row) => zona.includes(row.nombre_zona))
      .flatMap((row) => row.rbd_list || []);
  }, [rows, zona]);

  const commonQueryString = useMemo(() => {
    const params = new URLSearchParams({
      orgId: "1",
      from: fixedFrom,
      to: "now"
    });

    if (rbd) {
      params.append("var-RBD", rbd);
    }

    return params.toString();
  }, [rbd]);

  useEffect(() => {
    if (esOperador) {
      getOperator();
    } else if (esSubtelOAdmin) {
      getAllOperadores();
    }
  }, [esOperador, esSubtelOAdmin]);

  const content = (
    <Box width="100%">
      <Grid
        container
        spacing={1}
        sx={{ marginBottom: "28px", marginTop: "-2rem" }}
      >
        {esSubtelOAdmin && (
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl sx={{ width: "100%" }} size="small">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={operador}
                onChange={handleOperadorChange}
                disabled={loading}
              >
                {operadores.map((op) => (
                  <MenuItem key={op.id_operador} value={op.nombre_operador}>
                    {op.nombre_operador}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl sx={{ width: "100%" }} size="small">
            <Select
              multiple
              displayEmpty
              value={zona}
              onChange={handleZonaChange}
              disabled={loading}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Seleccionar Zona"
                  : selected.join(", ")
              }
            >
              {rows.map((row) => (
                <MenuItem key={row.nombre_zona} value={row.nombre_zona}>
                  <Checkbox checked={zona.indexOf(row.nombre_zona) > -1} />
                  <ListItemText
                    primary={
                      <Typography sx={{ color: "black!important" }}>
                        {row.nombre_zona}
                      </Typography>
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Autocomplete
            value={rbd}
            onChange={handleRbdChange}
            options={availableRBDs}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Seleccionar RBD"
              />
            )}
          />
        </Grid>
      </Grid>

      

      {loading ? (
        <HourglassLoader />
      ) : rbd ? (
      <>
      <Typography
        sx={{
          color: "#E53935",
          fontSize: "0.875rem",
          fontWeight: 600,
          mb: 2
        }}
      >
        La información desplegada en los gráficos corresponde al RBD
        seleccionado.
      </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {panelIds.map((panelId) => {
            const src = `${baseUrl}?${commonQueryString}&panelId=${panelId}`;

            return (
              <Box
                key={panelId}
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)"
                }}
              >
                <iframe
                  src={src}
                  width="100%"
                  height="220"
                  frameBorder="0"
                  title={`Grafico ${panelId}`}
                />
              </Box>
            );
          })}
        </Box>
        </>
      ) : (
        <Typography sx={{ color: "#666" }}>
          No hay RBD disponibles para mostrar.
        </Typography>
      )}
    </Box>
  );

  return esSubtelOAdmin ? (
    <Box sx={{ mt: 6 }}>{content}</Box>
  ) : (
    <PageBody>{content}</PageBody>
  );
}